import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  TextField,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import EditIcon from '@material-ui/icons/Edit';

/**
 * Color palette for map entries - distinct colors that work in both light and dark themes
 * These are used for the left border and subtle header tint
 */
const ENTRY_COLORS = [
  '#4285F4', // Blue
  '#34A853', // Green
  '#FBBC04', // Yellow
  '#EA4335', // Red
  '#9C27B0', // Purple
  '#00BCD4', // Cyan
  '#FF9800', // Orange
  '#607D8B', // Blue Grey
  '#E91E63', // Pink
  '#795548', // Brown
];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      overflow: 'hidden',
      marginBottom: theme.spacing(1),
    },
    containerColored: {
      borderLeftWidth: 4,
      borderLeftStyle: 'solid',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(1, 1.5),
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.03)'
          : 'rgba(0, 0, 0, 0.02)',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor:
          theme.palette.type === 'dark'
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.04)',
      },
      minHeight: 40,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      flex: 1,
      overflow: 'hidden',
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
    title: {
      fontWeight: 500,
      whiteSpace: 'nowrap',
    },
    titleInput: {
      '& .MuiInputBase-input': {
        padding: theme.spacing(0.5, 1),
        fontSize: '0.875rem',
        fontWeight: 500,
      },
    },
    subtitle: {
      color: theme.palette.text.secondary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: 300,
    },
    fieldCount: {
      color: theme.palette.text.secondary,
      fontSize: '0.75rem',
      marginLeft: theme.spacing(1),
    },
    content: {
      padding: theme.spacing(2),
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    expandIcon: {
      transition: 'transform 0.2s ease',
    },
    editButton: {
      padding: 4,
    },
  }),
);

interface CollapsibleItemProps {
  title: string;
  subtitle?: string;
  /** Number of fields to display in header when expanded */
  fieldCount?: number;
  defaultExpanded?: boolean;
  /** Force the item to be expanded (controlled mode) */
  forceExpanded?: boolean;
  /** Color index for visual differentiation (0-9, cycles) */
  colorIndex?: number;
  /** Whether the title is editable */
  editable?: boolean;
  /** Start with title in edit mode immediately */
  startInEditMode?: boolean;
  /** Callback when title changes (for editable titles) */
  onTitleChange?: (newTitle: string) => void;
  /** Callback when edit mode ends */
  onEditModeEnd?: () => void;
  children: React.ReactNode;
  actions?: React.ReactNode;
  /** Callback when expansion state changes */
  onExpandChange?: (expanded: boolean) => void;
  disabled?: boolean;
}

/**
 * A wrapper component for collapsible list/map items with consistent styling
 * Provides expand/collapse functionality with header showing title and summary
 * Supports color coding via colorIndex prop for visual differentiation
 * Supports inline title editing when editable=true
 */
export const CollapsibleItem: React.FC<CollapsibleItemProps> = ({
  title,
  subtitle,
  fieldCount,
  defaultExpanded = false,
  forceExpanded,
  colorIndex,
  editable = false,
  startInEditMode = false,
  onTitleChange,
  onEditModeEnd,
  children,
  actions,
  onExpandChange,
  disabled = false,
}) => {
  const classes = useStyles();
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const [isEditingTitle, setIsEditingTitle] = useState(startInEditMode);
  const [editedTitle, setEditedTitle] = useState(title);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const hasStartedInEditMode = useRef(startInEditMode);

  // Use forceExpanded if provided, otherwise use internal state
  const expanded = forceExpanded !== undefined ? forceExpanded : internalExpanded;

  // Sync internal state with forceExpanded when it changes
  useEffect(() => {
    if (forceExpanded !== undefined) {
      setInternalExpanded(forceExpanded);
    }
  }, [forceExpanded]);

  // Sync edited title with prop when not editing
  useEffect(() => {
    if (!isEditingTitle) {
      setEditedTitle(title);
    }
  }, [title, isEditingTitle]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Handle startInEditMode prop changes (for newly duplicated entries)
  useEffect(() => {
    if (startInEditMode && !hasStartedInEditMode.current) {
      setIsEditingTitle(true);
      hasStartedInEditMode.current = true;
    }
  }, [startInEditMode]);

  const handleToggle = () => {
    // Don't toggle if clicking on the title input or edit button
    if (isEditingTitle) return;

    const newExpanded = !expanded;
    setInternalExpanded(newExpanded);
    onExpandChange?.(newExpanded);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingTitle(true);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedTitle(e.target.value);
  };

  const handleTitleBlur = () => {
    finishEditing();
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      finishEditing();
    } else if (e.key === 'Escape') {
      setEditedTitle(title); // Reset to original
      setIsEditingTitle(false);
    }
  };

  const finishEditing = () => {
    setIsEditingTitle(false);
    if (editedTitle !== title && editedTitle.trim() && onTitleChange) {
      onTitleChange(editedTitle.trim());
    } else {
      setEditedTitle(title); // Reset if invalid or unchanged
    }
    onEditModeEnd?.();
  };

  // Get color for this item
  const entryColor = colorIndex !== undefined
    ? ENTRY_COLORS[colorIndex % ENTRY_COLORS.length]
    : undefined;

  // Build container style with optional color
  const containerStyle = entryColor
    ? { borderLeftColor: entryColor }
    : undefined;

  // Build header style with subtle color tint
  const headerStyle = entryColor
    ? { backgroundColor: `${entryColor}10` } // 10 = 6% opacity in hex
    : undefined;

  return (
    <Box
      className={`${classes.container} ${entryColor ? classes.containerColored : ''}`}
      style={containerStyle}
    >
      <Box
        className={classes.header}
        style={headerStyle}
        onClick={handleToggle}
      >
        <Box className={classes.headerLeft}>
          {isEditingTitle ? (
            <TextField
              inputRef={titleInputRef}
              size="small"
              variant="outlined"
              value={editedTitle}
              onChange={handleTitleChange}
              onBlur={handleTitleBlur}
              onKeyDown={handleTitleKeyDown}
              onClick={e => e.stopPropagation()}
              className={classes.titleInput}
              style={entryColor ? { color: entryColor } : undefined}
            />
          ) : (
            <>
              <Typography
                variant="body2"
                className={classes.title}
                style={entryColor ? { color: entryColor } : undefined}
              >
                {title}
              </Typography>
              {expanded && fieldCount !== undefined && (
                <Typography component="span" className={classes.fieldCount}>
                  ({fieldCount} field{fieldCount !== 1 ? 's' : ''})
                </Typography>
              )}
            </>
          )}
          {!expanded && !isEditingTitle && subtitle && (
            <Typography variant="caption" className={classes.subtitle}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box className={classes.headerRight}>
          {editable && !disabled && !isEditingTitle && (
            <IconButton
              size="small"
              className={classes.editButton}
              onClick={handleEditClick}
              title="Edit key"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {actions}
          <IconButton size="small" className={classes.expandIcon}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Box className={classes.content}>{children}</Box>
      </Collapse>
    </Box>
  );
};
